import { Component, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SignedIn, SignedOut, SignIn } from '@clerk/clerk-react';
import { AlertTriangle, SearchX } from 'lucide-react';
import { Button, Card, EmptyState, ToastProvider } from './components/ui';
import Layout from './components/Layout';
import CaseListPage from './pages/CaseListPage';
import NewCasePage from './pages/NewCasePage';
import CaseDetailPage from './pages/CaseDetailPage';
import FirmSettingsPage from './pages/FirmSettingsPage';
import AuditLogPage from './pages/AuditLogPage';
import ExtractorBenchmarkPage from './pages/ExtractorBenchmarkPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled error in route:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full p-8">
          <Card className="w-full max-w-md">
            <EmptyState
              icon={AlertTriangle}
              title="Something went wrong"
              description="An unexpected error occurred in this view. Reloading usually fixes it; your data is safe on the server."
              action={
                <Button size="lg" onClick={() => window.location.reload()}>
                  Reload
                </Button>
              }
            />
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-center h-full p-8">
      <Card className="w-full max-w-md">
        <EmptyState
          icon={SearchX}
          title="Page not found"
          description="This page does not exist or may have moved."
          action={
            <Button variant="secondary" size="lg" onClick={() => navigate('/')}>
              Back to all cases
            </Button>
          }
        />
      </Card>
    </div>
  );
}

const AppRoutes = () => (
  <Layout>
    <ErrorBoundary>
      <Routes>
        <Route path="/" element={<CaseListPage />} />
        <Route path="/cases/new" element={<NewCasePage />} />
        <Route path="/cases/:id/*" element={<CaseDetailPage />} />
        <Route path="/trash" element={<Navigate to="/firm-settings" replace />} />
        <Route path="/firm-settings" element={<FirmSettingsPage />} />
        <Route path="/admin/audit-log" element={<AuditLogPage />} />
        <Route path="/admin/extractor-benchmark" element={<ExtractorBenchmarkPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  </Layout>
);

function App({ authDisabled = false }: { authDisabled?: boolean }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          {authDisabled ? (
            <AppRoutes />
          ) : (
            <>
              <SignedIn>
                <AppRoutes />
              </SignedIn>
              <SignedOut>
                <div className="flex items-center justify-center min-h-screen bg-surface-page">
                  <SignIn routing="hash" />
                </div>
              </SignedOut>
            </>
          )}
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
